import { Calculator } from "lucide-react";

const TERMS = [36, 48, 60, 72, 84];

export const CALC_DEFAULTS = { price: 35000, down: 5000, term: 60, rate: 8.99 };

export const fmtMoney = (n) =>
  new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 0 }).format(Math.round(n)) + " $";

export const computeMonthly = ({ price, down, term, rate }) => {
  const principal = Math.max(price - down, 0);
  if (principal === 0) return 0;
  const r = rate / 100 / 12;
  if (r === 0) return principal / term;
  return (principal * r) / (1 - Math.pow(1 + r, -term));
};

export default function PaymentCalculator({ value, onChange }) {
  const { price, down, term, rate } = value;
  const monthly = computeMonthly(value);
  const upd = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="card-lux !p-8 sm:!p-10" data-testid="payment-calculator">
      <div className="flex items-center gap-3 mb-2">
        <Calculator size={20} className="text-gold" strokeWidth={1.5} />
        <h3 className="font-display text-2xl text-white">Estimez votre paiement mensuel</h3>
      </div>
      <p className="text-sm text-slate-400 font-light mb-8">
        Ajustez les montants et voyez l'estimation changer en temps réel.
      </p>

      <div className="space-y-7">
        <div>
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="calc-price" className="text-xs uppercase tracking-[0.18em] text-slate-400">
              Prix du véhicule
            </label>
            <span className="font-mono text-sm text-gold-light" data-testid="calc-price-value">
              {fmtMoney(price)}
            </span>
          </div>
          <input
            id="calc-price"
            data-testid="calc-price"
            type="range"
            min="5000"
            max="150000"
            step="500"
            value={price}
            onChange={(e) => upd({ price: Number(e.target.value) })}
            className="w-full accent-gold"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="calc-down" className="text-xs uppercase tracking-[0.18em] text-slate-400">
              Mise de fonds
            </label>
            <span className="font-mono text-sm text-gold-light" data-testid="calc-down-value">
              {fmtMoney(down)}
            </span>
          </div>
          <input
            id="calc-down"
            data-testid="calc-down"
            type="range"
            min="0"
            max={Math.min(price, 50000)}
            step="500"
            value={down}
            onChange={(e) => upd({ down: Number(e.target.value) })}
            className="w-full accent-gold"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="calc-rate" className="text-xs uppercase tracking-[0.18em] text-slate-400">
              Taux d'intérêt
            </label>
            <span className="font-mono text-sm text-gold-light" data-testid="calc-rate-value">
              {rate.toFixed(2).replace(".", ",")} %
            </span>
          </div>
          <input
            id="calc-rate"
            data-testid="calc-rate"
            type="range"
            min="2.99"
            max="19.99"
            step="0.5"
            value={rate}
            onChange={(e) => upd({ rate: Number(e.target.value) })}
            className="w-full accent-gold"
          />
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-3">Durée du prêt</p>
          <div className="flex flex-wrap gap-2" data-testid="calc-terms">
            {TERMS.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`calc-term-${t}`}
                onClick={() => upd({ term: t })}
                className={`px-4 py-2 rounded-full text-xs font-mono border transition-colors ${
                  term === t
                    ? "border-gold/60 bg-gold/10 text-gold-light"
                    : "border-white/10 text-slate-400 hover:border-gold/30 hover:text-slate-200"
                }`}
              >
                {t} mois
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-9 border-t border-white/[0.07] pt-7 flex items-end justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Paiement estimé</p>
          <p
            className="mt-2 font-display text-5xl sm:text-6xl text-gold-light"
            style={{ textShadow: "0 0 28px rgba(212, 175, 55, 0.3)" }}
            data-testid="calc-monthly-result"
          >
            {fmtMoney(monthly)}
          </p>
          <p className="mt-1 text-xs text-slate-500 font-light">par mois</p>
        </div>
        <div className="text-right font-mono text-[11px] text-slate-500 space-y-1">
          <p data-testid="calc-summary-term">{term} mois</p>
          <p data-testid="calc-summary-financed">{fmtMoney(Math.max(price - down, 0))} financés</p>
        </div>
      </div>

      <p className="mt-5 text-[11px] text-gold-light/80 font-light italic" data-testid="calc-prefill-note">
        Ces montants seront joints automatiquement à votre demande ci-dessous.
      </p>

      <p className="mt-3 text-[11px] text-slate-600 font-light leading-relaxed" data-testid="calc-disclaimer">
        Estimation à titre indicatif seulement, avant taxes. Le taux et le paiement réels dépendent
        de votre dossier et sont soumis à l'approbation des prêteurs.
      </p>
    </div>
  );
}
